import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';
import { useFormBuilder } from '../utils/useFormBuilder';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import {
  createEventComment,
  deleteEventComment,
  getEventCommentsByEventId,
  updateEventComment,
} from '../api/event-commentsAPI';
import { parseUtc } from '../utils/dateUtils';
import { formatTimeSincePosting } from '../utils/timeSincePosting';

const normalizePhotos = (value) => {
  if (!value) return [];

  const toArray = (input) => {
    if (!input) return [];
    if (Array.isArray(input)) return input;
    if (typeof input === 'string') {
      try {
        const parsed = JSON.parse(input);
        return toArray(parsed);
      } catch (err) {
        return [input];
      }
    }
    if (typeof input === 'object') {
      if (input === null) return [];
      const values = Object.values(input).flat();
      return values.flatMap((entry) => toArray(entry));
    }
    return [];
  };

  return toArray(value)
    .map((entry) => {
      if (!entry) return null;
      if (typeof entry === 'string') return entry;
      if (typeof entry === 'object') {
        if (typeof entry.key === 'string') return entry.key;
        if (typeof entry.filename === 'string') return entry.filename;
        if (typeof entry.url === 'string') return entry.url;
      }
      return null;
    })
    .filter((item, index, arr) => item && arr.indexOf(item) === index);
};

const normalizeComment = (comment) => {
  if (!comment) return null;
  const photos = normalizePhotos(comment.photos);
  const createdAt = comment.createdAt || comment.created_at || null;
  const user = comment.user || comment.User || null;

  return {
    ...comment,
    photos,
    createdAt,
    user,
  };
};

const normalizeCommentList = (list) => {
  const array = Array.isArray(list) ? list : [];
  const normalized = array
    .map((item) => normalizeComment(item))
    .filter(Boolean);

  return normalized.sort((a, b) => {
    const getTime = (value) => {
      const raw = value?.createdAt || value?.created_at;
      if (!raw) return 0;
      const date = parseUtc(raw);
      const timestamp = date.getTime();
      return Number.isFinite(timestamp) ? timestamp : 0;
    };
    return getTime(b) - getTime(a);
  });
};

const formatTimestamp = (value) => {
  if (!value) return '';
  const date = parseUtc(value);
  if (!Number.isFinite(date.getTime())) return '';

  const day = String(date.getDate()).padStart(2, '0');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${day}-${month}-${year} ${hours}:${minutes}`;
};

const getUserImageKey = (user) => {
  if (!user) return null;
  return user.userImage || user.user_image || user.image || null;
};

const getUserInitials = (name) => {
  if (!name) return '';
  const parts = String(name)
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
};

const EventCommentsSection = ({
  eventId,
  localComments = [],
  onLocalCommentsChange,
  className = '',
  onCommentActions,
}) => {
  const userProfile = useUserProfile();
  const userId = userProfile?.id ?? null;
  const isLocalMode = !eventId;
  const normalizedLocalComments = useMemo(
    () => normalizeCommentList(localComments),
    [localComments],
  );
  const [formData, setFormData] = useState({ comment: '', photos: [] });
  const [errors, setErrors] = useState({});
  const [comments, setComments] = useState(() => normalizedLocalComments);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [updatingComment, setUpdatingComment] = useState(false);
  const [deletingCommentId, setDeletingCommentId] = useState(null);
  const [submitError, setSubmitError] = useState('');
  const [editCommentModal, setEditCommentModal] = useState(null);
  const [editCommentForm, setEditCommentForm] = useState({ comment: '', photos: [] });
  const [editCommentErrors, setEditCommentErrors] = useState({});
  const [openCommentActionsMenuId, setOpenCommentActionsMenuId] = useState(null);

  useEffect(() => {
    setFormData({ comment: '', photos: [] });
    setErrors({});
    setSubmitError('');
    setEditCommentModal(null);
    setEditCommentForm({ comment: '', photos: [] });
    setEditCommentErrors({});
  }, [eventId]);

  const fetchComments = useCallback(async () => {
    if (!eventId) {
      setComments(normalizedLocalComments);
      return;
    }
    setLoading(true);
    try {
      const response = await getEventCommentsByEventId(eventId);
      const data = Array.isArray(response?.data) ? response.data : response;
      setComments(normalizeCommentList(data));
    } catch (err) {
      console.error('Error loading event comments', err);
    } finally {
      setLoading(false);
    }
  }, [eventId, normalizedLocalComments]);

  useEffect(() => {
    if (isLocalMode) {
      setComments(normalizedLocalComments);
      setLoading(false);
      return;
    }
    fetchComments();
  }, [fetchComments, isLocalMode, normalizedLocalComments]);

  useEffect(() => {
    if (!openCommentActionsMenuId) return undefined;
    const handleOutsideClick = () => setOpenCommentActionsMenuId(null);
    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, [openCommentActionsMenuId]);

  const fields = useMemo(
    () => [
      {
        field: 'comment',
        label: '',
        hideLabel: true,
        type: 'textarea',
        size: 12,
        lines: '3',
        placeholder: 'Add a comment',
        required: false,
      },
      {
        field: 'photos',
        label: '',
        type: 'images',
        size: 6,
        directory: 'workorderimages',
      },
    ],
    [],
  );

  const { renderField } = useFormBuilder(fields, formData, setFormData, errors);
  const { renderField: renderEditField } = useFormBuilder(
    fields,
    editCommentForm,
    setEditCommentForm,
    editCommentErrors,
  );

  const isCommentOwnedByLoggedInUser = useCallback(
    (comment) => {
      if (!comment || !userId) return false;
      const commentUserId =
        comment?.userId ??
        comment?.user?.id ??
        comment?.User?.id ??
        comment?.user?.userId ??
        comment?.User?.userId;

      return String(commentUserId) === String(userId);
    },
    [userId],
  );

  const handleLocalComment = useCallback(
    (commentText) => {
      if (!userId) {
        setSubmitError('You must be signed in to add comments.');
        return false;
      }

      setSubmitError('');
      const photos = Array.isArray(formData.photos) ? formData.photos : [];
      const userName = userProfile?.name || 'You';
      const userImage = userProfile?.user_image || userProfile?.userImage || null;
      const newComment = normalizeComment({
        id: Date.now(),
        comments: commentText,
        photos,
        createdAt: new Date().toISOString(),
        user: {
          name: userName,
          userImage,
        },
        userId,
      });

      setComments((prev) => {
        const existing = Array.isArray(prev) ? prev : [];
        const updated = newComment ? [newComment, ...existing] : existing;
        if (typeof onLocalCommentsChange === 'function') {
          onLocalCommentsChange(updated);
        }
        return updated;
      });
      setFormData({ comment: '', photos: [] });
      setErrors({});
      return true;
    },
    [formData.photos, onLocalCommentsChange, userId, userProfile],
  );

  const handleAddComment = useCallback(async () => {
    const trimmedComment = (formData.comment || '').trim();
    const photos = Array.isArray(formData.photos) ? formData.photos : [];
    const hasPhotos = photos.length > 0;
    if (!trimmedComment && !hasPhotos) {
      setErrors({ comment: 'Add a comment or at least one photo' });
      return false;
    }

    if (isLocalMode) {
      return handleLocalComment(trimmedComment);
    }

    if (!eventId) return false;
    if (!userId) {
      setSubmitError('You must be signed in to add comments.');
      return false;
    }

    setSubmitting(true);
    setSubmitError('');
    try {
      const numericEventId = Number(eventId);
      const payload = {
        eventsId: Number.isFinite(numericEventId) ? numericEventId : eventId,
        userId,
        comments: trimmedComment,
        photos,
      };
      const apiResult = await createEventComment(payload);
      const commentData =
        (apiResult && typeof apiResult === 'object' && 'data' in apiResult
          ? apiResult.data
          : apiResult) || null;
      const baseComment = normalizeComment(commentData) || normalizeComment(payload);
      const hydratedComment = (() => {
        if (!baseComment) return null;
        const fallbackUser =
          baseComment.user ||
          (userProfile
            ? {
              name: userProfile.name || 'You',
              userImage: userProfile.user_image || userProfile.userImage || null,
            }
            : null);
        const fallbackCreatedAt = baseComment.createdAt || new Date().toISOString();
        if (baseComment.id === undefined || baseComment.id === null) {
          return {
            ...baseComment,
            id: Date.now(),
            createdAt: fallbackCreatedAt,
            user: fallbackUser,
          };
        }
        return {
          ...baseComment,
          createdAt: fallbackCreatedAt,
          user: fallbackUser,
        };
      })();

      setComments((prev) => {
        if (!hydratedComment) return prev || [];
        const existing = Array.isArray(prev) ? prev : [];
        const filtered = hydratedComment.id
          ? existing.filter((c) => c && c.id !== hydratedComment.id)
          : existing;
        return [hydratedComment, ...filtered];
      });
      setFormData({ comment: '', photos: [] });
      setErrors({});
      return true;
    } catch (err) {
      console.error('Error creating event comment', err);
      setSubmitError('Unable to save comment. Please try again.');
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [eventId, formData.comment, formData.photos, handleLocalComment, isLocalMode, userId, userProfile]);

  const hasPendingComment = useCallback(() => {
    const trimmedComment = (formData.comment || '').trim();
    const photos = Array.isArray(formData.photos) ? formData.photos : [];
    return Boolean(trimmedComment) || photos.length > 0;
  }, [formData.comment, formData.photos]);

  const commentActions = useMemo(
    () => ({ submit: handleAddComment, hasPendingComment }),
    [handleAddComment, hasPendingComment],
  );

  useEffect(() => {
    if (typeof onCommentActions !== 'function') return undefined;
    return onCommentActions(commentActions);
  }, [commentActions, onCommentActions]);

  return (
    <div className={`event-comments-section ${className}`} style={{ width: '800px'}}>
      <div className="row g-3 align-items-start">
        {fields.map((field) => renderField(field))}
        <div className="col-md-6 d-flex justify-content-end mt-24">
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={handleAddComment}
            disabled={submitting}
          >
            <Icon icon="mdi:send-outline" className="icon text-xl line-height-1 me-4" />
            {submitting ? 'Adding...' : 'Post'}
          </button>
        </div>
        {submitError && (
          <div className="col-12">
            <div className="text-danger text-sm">{submitError}</div>
          </div>
        )}
      </div>
      <hr className="my-24"/>
      <div className="mt-36">
        {loading ? (
          <div className="text-muted text-sm">Loading comments…</div>
        ) : comments.length === 0 ? (
          <div className="text-muted text-sm">No comments yet.</div>
        ) : (
          <div className="d-flex flex-column gap-3" style={{ maxHeight: 'calc(100vh - 550px)', overflowY: 'auto' }}>
            {comments.map((comment) => {
              const userName = comment?.user?.name || 'Unknown User';
              const imageKey = getUserImageKey(comment?.user);
              const userImageUrl =
                imageKey && /^https?:/i.test(imageKey)
                  ? imageKey
                  : imageKey
                    ? `${process.env.REACT_APP_SPACES_BASE}/${imageKey}`
                    : null;
              const initials = getUserInitials(userName);
              const timestamp = comment?.createdAt;
              const timeSincePosting = formatTimeSincePosting(timestamp);
              const formattedTimestamp = formatTimestamp(timestamp);
              const photos = Array.isArray(comment?.photos) ? comment.photos : [];
              const canModifyComment = isCommentOwnedByLoggedInUser(comment);

              return (
                <div key={comment.id || `${timestamp}-${userName}`}>
                  <div className="d-flex gap-3 align-items-start">
                    <div
                      className="flex-shrink-0 d-flex align-items-center justify-content-center rounded-circle bg-primary text-white"
                      style={{ width: '35px', height: '35px', overflow: 'hidden' }}
                    >
                      {userImageUrl ? (
                        <img
                          src={userImageUrl}
                          alt={userName}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <span className="fw-semibold">{initials}</span>
                      )}
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                        <div className="d-flex flex-wrap justify-content-start align-items-center gap-2">
                          <strong>{userName}</strong>
                        </div>
                        <div className="d-inline-flex align-items-center gap-2">
                          <span className="text-muted text-sm" title={formattedTimestamp}>
                            {timeSincePosting || formattedTimestamp}
                          </span>
                          {canModifyComment && (
                            <div className="dropdown position-relative">
                              <button
                                type="button"
                                className="btn btn-link text-secondary p-0 d-inline-flex align-items-center justify-content-center"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setOpenCommentActionsMenuId((current) =>
                                    current === comment?.id ? null : comment?.id
                                  );
                                }}
                                aria-expanded={openCommentActionsMenuId === comment?.id}
                                title="Comment actions"
                              >
                                <Icon icon="mdi:dots-vertical" className="icon text-xl line-height-1" />
                              </button>
                              <ul
                                className={`dropdown-menu ${openCommentActionsMenuId === comment?.id ? 'show' : ''
                                  }`}
                                style={{
                                  right: 0,
                                  left: 'auto',
                                }}
                              >
                                <li>
                                  <button
                                    type="button"
                                    className="dropdown-item"
                                    onClick={() => {
                                      setEditCommentModal(comment);
                                      setEditCommentForm({
                                        comment: comment?.comments || '',
                                        photos,
                                      });
                                      setEditCommentErrors({});
                                      setSubmitError('');
                                      setOpenCommentActionsMenuId(null);
                                    }}
                                  >
                                    Edit
                                  </button>
                                </li>
                                <li>
                                  <button
                                    type="button"
                                    className="dropdown-item text-danger"
                                    onClick={async () => {
                                      const commentId = comment?.id;
                                      if (!commentId) return;
                                      if (!window.confirm('Delete this comment?')) return;

                                      if (isLocalMode) {
                                        setComments((prev) => {
                                          const existing = Array.isArray(prev) ? prev : [];
                                          const updated = existing.filter((item) => item?.id !== commentId);
                                          if (typeof onLocalCommentsChange === 'function') {
                                            onLocalCommentsChange(updated);
                                          }
                                          return updated;
                                        });
                                        setOpenCommentActionsMenuId(null);
                                        return;
                                      }

                                      setDeletingCommentId(commentId);
                                      setSubmitError('');
                                      try {
                                        await deleteEventComment(commentId);
                                        setComments((prev) => {
                                          const existing = Array.isArray(prev) ? prev : [];
                                          return existing.filter((item) => item?.id !== commentId);
                                        });
                                      } catch (err) {
                                        console.error('Error deleting event comment', err);
                                        setSubmitError('Unable to delete comment. Please try again.');
                                      } finally {
                                        setDeletingCommentId(null);
                                        setOpenCommentActionsMenuId(null);
                                      }
                                    }}
                                    disabled={deletingCommentId === comment?.id}
                                  >
                                    Delete
                                  </button>
                                </li>
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="mt-2" style={{ whiteSpace: 'pre-wrap' }}>
                        {comment?.comments || comment?.comment || ''}
                      </div>
                      {photos.length > 0 && (
                        <div className="d-flex flex-wrap gap-2 mt-8">
                          {photos.map((photoKey, index) => {
                            const photoUrl =
                              photoKey && /^https?:/i.test(photoKey)
                                ? photoKey
                                : `${process.env.REACT_APP_SPACES_BASE}/${photoKey}`;
                            return (
                              <div
                                key={`${comment.id || timestamp || index}-${photoKey}-${index}`}
                                style={{
                                  width: '90px',
                                  height: '90px',
                                  borderRadius: '6px',
                                  overflow: 'hidden',
                                  border: '1px solid #ddd',
                                }}
                              >
                                <img
                                  src={photoUrl}
                                  alt={`Comment attachment ${index + 1}`}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {editCommentModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-s">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Comment</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setEditCommentModal(null);
                    setEditCommentErrors({});
                  }}
                />
              </div>
              <div className="modal-body">
                <div className="row g-3">{fields.map((field) => renderEditField(field))}</div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setEditCommentModal(null);
                    setEditCommentErrors({});
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={updatingComment}
                  onClick={async () => {
                    const trimmedComment = (editCommentForm.comment || '').trim();
                    const updatedPhotos = Array.isArray(editCommentForm.photos) ? editCommentForm.photos : [];
                    if (!trimmedComment && updatedPhotos.length === 0) {
                      setEditCommentErrors({ comment: 'Add a comment or at least one photo' });
                      return;
                    }

                    const updatedComment = {
                      ...editCommentModal,
                      comments: trimmedComment,
                      photos: updatedPhotos,
                    };

                    if (isLocalMode) {
                      setComments((prev) => {
                        const existing = Array.isArray(prev) ? prev : [];
                        const updated = existing.map((item) =>
                          item?.id === editCommentModal?.id ? normalizeComment(updatedComment) : item,
                        );
                        if (typeof onLocalCommentsChange === 'function') {
                          onLocalCommentsChange(updated);
                        }
                        return updated;
                      });
                      setEditCommentModal(null);
                      setEditCommentErrors({});
                      return;
                    }

                    if (!editCommentModal?.id) return;
                    setUpdatingComment(true);
                    setSubmitError('');
                    try {
                      const payload = {
                        comments: trimmedComment,
                        photos: updatedPhotos,
                      };
                      const response = await updateEventComment(editCommentModal.id, payload);
                      const responseComment = normalizeComment(response?.data || response || payload);
                      setComments((prev) => {
                        const existing = Array.isArray(prev) ? prev : [];
                        return existing.map((item) => {
                          if (item?.id !== editCommentModal.id) return item;
                          return {
                            ...item,
                            ...responseComment,
                            comments: responseComment?.comments || trimmedComment,
                            photos: responseComment?.photos || payload.photos,
                          };
                        });
                      });
                      setEditCommentModal(null);
                      setEditCommentErrors({});
                    } catch (err) {
                      console.error('Error updating event comment', err);
                      setSubmitError('Unable to update comment. Please try again.');
                    } finally {
                      setUpdatingComment(false);
                    }
                  }}
                >
                  {updatingComment ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

EventCommentsSection.propTypes = {
  eventId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  localComments: PropTypes.arrayOf(PropTypes.object),
  onLocalCommentsChange: PropTypes.func,
  className: PropTypes.string,
  onCommentActions: PropTypes.func,
};

export default EventCommentsSection;
